<template>
  <div class="chart-wrapper">
    <canvas ref="canvas"></canvas>
    <!-- CP-274: chart.js chunk gone (tab from before a deploy): note over the 300px box, no auto reload. -->
    <stale-chunk-note v-if="loadFailed" />
  </div>
</template>

<script>
import { handleInPageChunkError } from '@/utils/chunkReload';
import StaleChunkNote from './StaleChunkNote.vue';

// CP-224: chart.js loads on first draw instead of sitting in the entry bundle (the canvas box below keeps its
// 300 px min-height meanwhile, so nothing moves). One retry if the chunk request fails.
let chartPromise = null;
function loadChart(retry = 1) {
  if (!chartPromise) {
    chartPromise = import(/* webpackChunkName: "bar-chart" */ './BarChartCanvas.js').then((m) => m.Chart).catch((err) => {
      chartPromise = null;
      if (retry > 0) return loadChart(retry - 1);
      throw err;
    });
  }
  return chartPromise;
}

function destroyChart(chart) {
  if (!chart) return;
  try {
    chart.stop();
    if (chart.canvas && chart.options) {
      chart.options.responsive = false;
    }
    chart.destroy();
  } catch (err) {
    // A resize frame can run after the canvas is already gone.
  }
}

export default {
  name: 'BarChart',
  components: { StaleChunkNote },
  props: {
    chartData: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      chart: null,
      loadFailed: false,
      renderScheduled: false,
      drawSeq: 0
    };
  },
  watch: {
    chartData: {
      handler() {
        this.renderChart();
      },
      deep: true
    },
    options: {
      handler() {
        this.renderChart();
      },
      deep: true
    }
  },
  mounted() {
    this.renderChart();
  },
  beforeUnmount() {
    this.drawSeq += 1; // a draw still waiting for the chart.js chunk must not run on a gone canvas
    destroyChart(this.chart);
    this.chart = null;
  },
  methods: {
    // chartData and options often change in the same tick (viewport axis
    // switch). Drawing twice detaches the canvas and Chart.clear throws.
    renderChart() {
      if (this.renderScheduled) return;
      this.renderScheduled = true;
      this.$nextTick(() => {
        this.renderScheduled = false;
        this.drawChart();
      });
    },
    async drawChart() {
      const canvas = this.$refs.canvas;
      destroyChart(this.chart);
      this.chart = null;
      if (!canvas || !this.chartData) return;
      this.drawSeq += 1;
      const seq = this.drawSeq;
      let Chart;
      try {
        Chart = await loadChart();
      } catch (err) {
        // chunk failed twice: 'refresh to update' note in the canvas box (CP-274), never a reload
        if (seq === this.drawSeq) handleInPageChunkError(err, { showNote: () => { this.loadFailed = true; } });
        return;
      }
      // Superseded by a newer draw, or unmounted / canvas replaced while the chunk loaded.
      if (seq !== this.drawSeq || this.$refs.canvas !== canvas) return;
      const leftover = Chart.getChart(canvas);
      if (leftover) destroyChart(leftover);
      if (!canvas.isConnected) return;

      const defaultOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false
      };

      this.chart = new Chart(canvas, {
        type: 'bar',
        data: this.chartData,
        options: { ...defaultOptions, ...this.options }
      });
    }
  }
};
</script>

<style scoped>
.chart-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: flex;
}

canvas {
  width: 100% !important;
  height: auto !important;
  max-height: 100%;
}
</style>
